import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Alert,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Print as PrintIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { adminService } from '../../services/admin.service';
import { PermissionMatrix as PermissionMatrixType, Role } from '../../types/admin.types';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { enqueueSnackbar } from 'notistack';

// ── constants ──────────────────────────────────────────────────────────────────
const DISPLAY_PERMISSIONS: string[] = [
  'workflow.view',
  'workflow.create',
  'workflow.edit',
  'workflow.delete',
  'workflow.execute',
  'execution.view',
  'execution.cancel',
  'credential.view',
  'credential.create',
  'credential.edit',
  'credential.delete',
  'integration.view',
  'integration.create',
  'integration.edit',
  'integration.delete',
  'user.manage',
  'org.manage',
];

const PERM_LABEL = (p: string) =>
  p.split('.').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const ROLE_ORDER = ['viewer', 'editor', 'admin', 'owner', 'system_admin'];

const ROLE_COLOR: Record<string, 'default' | 'primary' | 'warning' | 'error' | 'info'> = {
  viewer: 'default',
  editor: 'primary',
  admin: 'warning',
  owner: 'error',
  system_admin: 'info',
};

// ── Role Defaults Tab ──────────────────────────────────────────────────────────
interface RoleDefaultsProps {
  roles: Role[];
  allPerms: string[];
}

const RoleDefaultsTab: React.FC<RoleDefaultsProps> = ({ roles, allPerms }) => {
  const printRef = useRef<HTMLDivElement>(null);

  const sortedRoles = [...roles].sort(
    (a, b) => ROLE_ORDER.indexOf(a.name) - ROLE_ORDER.indexOf(b.name)
  );

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWin = window.open('', '_blank', 'width=900,height=700');
    if (!printWin) return;
    printWin.document.write(`
      <html>
        <head>
          <title>Role Permissions Reference</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
            h2 { margin-bottom: 4px; }
            p { color: #666; margin-bottom: 16px; font-size: 10px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 5px 8px; text-align: center; }
            th:first-child, td:first-child { text-align: left; min-width: 160px; background: #f5f5f5; }
            th { background: #1976d2; color: white; font-size: 10px; }
            th:first-child { background: #f5f5f5; color: #333; }
            tr:nth-child(even) td { background: #fafafa; }
            tr:nth-child(even) td:first-child { background: #f0f0f0; }
            .yes { color: #2e7d32; font-weight: bold; font-size: 14px; }
            .no  { color: #ccc; font-size: 14px; }
            .all { color: #1976d2; font-weight: bold; font-size: 12px; }
            @media print { body { margin: 10px; } }
          </style>
        </head>
        <body>
          <h2>Werk Gang — Role Permissions Reference</h2>
          <p>Generated ${new Date().toLocaleString()} &nbsp;|&nbsp; Use this to choose the right role when creating users.</p>
          ${content.innerHTML}
        </body>
      </html>
    `);
    printWin.document.close();
    printWin.focus();
    setTimeout(() => { printWin.print(); printWin.close(); }, 500);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Default permissions each role grants. Admins can add per-user overrides on the User Matrix tab.
        </Typography>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} size="small">
          Print / Export
        </Button>
      </Box>

      <Paper sx={{ overflow: 'auto' }}>
        <div ref={printRef}>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 180, fontWeight: 700 }}>Permission</TableCell>
                  {sortedRoles.map((role) => (
                    <TableCell key={role.id} align="center" sx={{ minWidth: 90 }}>
                      <Chip
                        label={role.name}
                        color={ROLE_COLOR[role.name] || 'default'}
                        size="small"
                        sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {allPerms.map((perm) => (
                  <TableRow key={perm} hover>
                    <TableCell sx={{ fontWeight: 500, fontSize: 13 }}>
                      {PERM_LABEL(perm)}
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
                        {perm}
                      </Typography>
                    </TableCell>
                    {sortedRoles.map((role) => {
                      const hasAll = (role.permissions || []).includes('*');
                      const hasPerm = hasAll || (role.permissions || []).includes(perm);
                      return (
                        <TableCell key={role.id} align="center">
                          {hasAll ? (
                            <Tooltip title="All permissions">
                              <Typography sx={{ color: 'primary.main', fontWeight: 700, fontSize: 13 }}>★</Typography>
                            </Tooltip>
                          ) : hasPerm ? (
                            <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          ) : (
                            <CancelIcon sx={{ color: 'action.disabled', fontSize: 18 }} />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Paper>

      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Tip for bulk user creation:</strong> Assign the appropriate role when creating users — they'll automatically
          get all permissions shown above. Use the User Matrix tab only for individual exceptions.
        </Typography>
      </Box>
    </Box>
  );
};

// ── User Matrix Tab ────────────────────────────────────────────────────────────
interface UserMatrixProps {
  matrix: PermissionMatrixType[];
  permissions: string[];
  onToggle: (userId: string, perm: string) => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
  onClearError: () => void;
}

const UserMatrixTab: React.FC<UserMatrixProps> = ({
  matrix, permissions, onToggle, onSave, saving, error, onClearError,
}) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Per-user overrides on top of their role's defaults. System admins always have all permissions.
      </Typography>
      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={onSave}
        disabled={saving}
        size="small"
      >
        {saving ? 'Saving…' : 'Save Changes'}
      </Button>
    </Box>

    {error && (
      <Alert severity="error" sx={{ mb: 2 }} onClose={onClearError}>
        {error}
      </Alert>
    )}

    <Paper sx={{ overflow: 'auto' }}>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 200, fontWeight: 700 }}>User</TableCell>
              <TableCell sx={{ minWidth: 100, fontWeight: 700 }}>Role</TableCell>
              {permissions.map((perm) => (
                <TableCell key={perm} align="center" sx={{ minWidth: 80 }}>
                  <Tooltip title={perm}>
                    <Typography variant="caption" sx={{ fontSize: 10, display: 'block', lineHeight: 1.2 }}>
                      {PERM_LABEL(perm)}
                    </Typography>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {matrix.map((user) => (
              <TableRow key={user.user_id} hover>
                <TableCell sx={{ fontSize: 13 }}>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    color={ROLE_COLOR[user.role || 'viewer'] || 'default'}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </TableCell>
                {permissions.map((perm) => (
                  <TableCell key={perm} align="center">
                    <Checkbox
                      checked={user.permissions[perm] || false}
                      disabled={user.role === 'system_admin'}
                      onChange={() => onToggle(user.user_id, perm)}
                      size="small"
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  </Box>
);

// ── Main Component ─────────────────────────────────────────────────────────────
export const PermissionMatrix: React.FC = () => {
  const [tab, setTab] = useState<'users' | 'roles'>('roles');
  const [matrix, setMatrix] = useState<PermissionMatrixType[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [perms, liveMatrix, liveRoles] = await Promise.all([
        adminService.getPermissions(),
        adminService.getPermissionMatrix(),
        adminService.getRoles(),
      ]);
      const permStrings = Array.isArray(perms) ? perms.map((p: any) => String(p)) : [];
      setPermissions(DISPLAY_PERMISSIONS.filter((p) => permStrings.includes(p)));
      setMatrix(liveMatrix);
      setRoles(Array.isArray(liveRoles) ? liveRoles : []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = (userId: string, perm: string) => {
    setMatrix((prev) =>
      prev.map((u) =>
        u.user_id === userId
          ? { ...u, permissions: { ...u.permissions, [perm]: !u.permissions[perm] } }
          : u
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      for (const user of matrix) {
        if ((user.role || '') === 'system_admin') continue;
        await adminService.updatePermissionMatrix(user.user_id, user.permissions);
      }
      setError(null);
      enqueueSnackbar('Permissions saved', { variant: 'success' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>Permissions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        <strong>Role Defaults</strong> — what every user of a role gets automatically.&nbsp;
        <strong>User Matrix</strong> — individual overrides on top of those defaults.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="roles" label="Role Defaults" />
        <Tab value="users" label="User Matrix" />
      </Tabs>

      {tab === 'roles' ? (
        <RoleDefaultsTab roles={roles} allPerms={permissions} />
      ) : (
        <UserMatrixTab
          matrix={matrix}
          permissions={permissions}
          onToggle={handleToggle}
          onSave={handleSave}
          saving={saving}
          error={error}
          onClearError={() => setError(null)}
        />
      )}
    </Box>
  );
};
